import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, ChevronRight, User, PawPrint, Banknote, Syringe, Bell, MessageSquare, Settings, Tag, ChevronDown, Building2, CalendarPlus, Wallet, HelpCircle, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getCustomers, getDailyStats, getUpcomingVaccines, getTodaysVaccines, getStockAlerts, getUnreadCount, getUnreadTicketCount, getMySmsCredits, getProducts, getMySubscription, getBoardingRevenue } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/Loader';
import BottomNav from '../components/BottomNav';
import PetAvatar from '../components/PetAvatar';
import PetIcon from '../components/PetIcon';
import QuickSaleModal from '../components/QuickSaleModal';
import QuickCustomerModal from '../components/QuickCustomerModal';
import QuickPetModal from '../components/QuickPetModal';
import QuickOperationModal from '../components/QuickOperationModal';
import QuickBalanceModal from '../components/QuickBalanceModal';
import DirectBookingModal from '../components/calendar/DirectBookingModal';
import UnifiedHistoryModal from '../components/UnifiedHistoryModal';
import NotificationsModal from '../components/NotificationsModal';
import TicketsModal from '../components/TicketsModal';
import TutorialModal from '../components/TutorialModal';
import UpdateHistoryModal from '../components/UpdateHistoryModal';
import { DailySummary } from '../components/home/DailySummary';
import { RemindersSection } from '../components/home/RemindersSection';
import { AlertsSection } from '../components/home/AlertsSection';
import { PriceSearchModal } from '../components/home/PriceSearchModal';
import { SubscriptionWarningOverlay } from '../components/home/SubscriptionWarningOverlay';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_140853a5-d37e-4724-8f64-c2f7eabb8728/artifacts/4msp2ofk_agrosvetlogo.png';

const Home = () => {
  const navigate = useNavigate();
  const { user, activeClinicId, switchClinic, getActiveClinicName, hasMultipleClinics } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [stats, setStats] = useState(null);
  const [todaysVaccines, setTodaysVaccines] = useState([]);
  const [upcomingVaccines, setUpcomingVaccines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIncome, setShowIncome] = useState(false);
  const [stockAlerts, setStockAlerts] = useState({ low_stock: [], expiring: [] });
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [unreadTicketCount, setUnreadTicketCount] = useState(0);
  const [showPriceSearch, setShowPriceSearch] = useState(false);
  const [priceQuery, setPriceQuery] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [showQuickSale, setShowQuickSale] = useState(false);
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);
  const [customerPrefill, setCustomerPrefill] = useState({ name: '', phone: '' });
  const [showQuickPet, setShowQuickPet] = useState(false);
  const [showQuickOp, setShowQuickOp] = useState(false);
  const [showDirectBooking, setShowDirectBooking] = useState(false);
  const [showQuickBalance, setShowQuickBalance] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showTickets, setShowTickets] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [subscriptionWarning, setSubscriptionWarning] = useState(null);
  const [showClinicPicker, setShowClinicPicker] = useState(false);
  const [boardingCount, setBoardingCount] = useState(0);
  const [showUpdateHistory, setShowUpdateHistory] = useState(false);

  useEffect(() => {
    loadData();
    const handleOpenCustomer = () => setShowQuickCustomer(true);
    const handleClinicSwitch = () => { setLoading(true); loadData(); };
    window.addEventListener('openQuickCustomer', handleOpenCustomer);
    window.addEventListener('clinicSwitched', handleClinicSwitch);
    return () => {
      window.removeEventListener('openQuickCustomer', handleOpenCustomer);
      window.removeEventListener('clinicSwitched', handleClinicSwitch);
    };
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, todayRes, upcomingRes, alertsRes] = await Promise.all([
        getDailyStats(),
        getTodaysVaccines(),
        getUpcomingVaccines(7),
        getStockAlerts().catch(() => ({ data: { low_stock: [], expiring: [] } }))
      ]);
      setStats(statsRes.data);
      setTodaysVaccines(todayRes.data);
      setUpcomingVaccines(upcomingRes.data.slice(0, 5));
      setStockAlerts(alertsRes.data || { low_stock: [], expiring: [] });
      try { const boardingRes = await getBoardingRevenue(new Date().toISOString().split('T')[0], new Date().toISOString().split('T')[0]); setBoardingCount(boardingRes.data?.count || 0); } catch (e) {}
      try { const notifRes = await getUnreadCount(); setUnreadNotifCount(notifRes.data.count); } catch (e) {}
      try { const ticketRes = await getUnreadTicketCount(); setUnreadTicketCount(ticketRes.data.count); } catch (e) {}
      try { const creditsRes = await getMySmsCredits(); } catch (e) {}
      try {
        const subRes = await getMySubscription();
        const sub = subRes.data;
        if (sub.locked || sub.grace_period || (sub.days_remaining !== null && sub.days_remaining <= 3 && sub.days_remaining >= 0)) {
          setSubscriptionWarning(sub);
        }
      } catch (e) {}
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openPriceSearch = async () => {
    setShowPriceSearch(true);
    if (allProducts.length === 0) {
      try { const r = await getProducts(); setAllProducts(r.data); } catch (e) {}
    }
  };

  const filteredPriceResults = priceQuery.length >= 1
    ? allProducts.filter(p =>
        p.name.toLowerCase().includes(priceQuery.toLowerCase()) ||
        (p.barcode && p.barcode.includes(priceQuery))
      ).slice(0, 15)
    : [];

  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const response = await getCustomers(query);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { handleSearch(searchQuery); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const handleCustomerSelect = (customer) => { navigate(`/customer/${customer.id}`); };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);
  };

  if (loading) return <Loader fullScreen />;

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-24 lg:pb-4">
      {/* Header */}
      <header className="bg-white px-4 pt-12 lg:pt-4 pb-3 shadow-sm">
        {/* Row 1: Clinic name */}
        <div className="flex items-center gap-3 mb-2 lg:hidden">
          <img src={LOGO_URL} alt="AGROSVET" className="w-12 h-12 object-contain flex-shrink-0" />
          <div className="min-w-0 flex-1">
            {hasMultipleClinics() ? (
              <button
                onClick={() => setShowClinicPicker(v => !v)}
                className="flex items-center gap-1 max-w-full"
                data-testid="clinic-switcher-btn"
              >
                <span className="text-lg font-bold text-gray-900 truncate">{getActiveClinicName() || 'AGROSVET'}</span>
                <ChevronDown size={16} className="text-[#8E8E93] flex-shrink-0" />
              </button>
            ) : (
              <p className="text-lg font-bold text-gray-900 truncate">
                {getActiveClinicName() || user?.clinic_name || 'AGROSVET'}
              </p>
            )}
            <p className="text-xs text-[#8E8E93] truncate">{user?.name}</p>
          </div>
        </div>

        {showClinicPicker && hasMultipleClinics() && (
          <div className="mb-2 bg-[#F2F2F7] rounded-2xl p-2 space-y-1 lg:hidden" data-testid="clinic-picker-dropdown">
            {(user?.clinic_names || []).map(c => (
              <button
                key={c.id}
                onClick={() => { switchClinic(c.id); setShowClinicPicker(false); }}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center gap-3 ${
                  activeClinicId === c.id ? 'bg-[#6b6de3] text-white shadow-sm' : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                data-testid={`clinic-option-${c.id}`}
              >
                <Building2 size={16} />
                {c.name}
              </button>
            ))}
          </div>
        )}

        {/* Row 2: Search + Price + Icons - all in one line */}
        <div className="flex items-center gap-1.5 lg:hidden">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={16} />
            <Input
              type="search" placeholder="Müşteri Ara (İsim, telefon veya TCKN)"
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && searchResults.length === 1) { handleCustomerSelect(searchResults[0]); } }}
              className="pl-8 h-9 text-xs bg-[#F2F2F7] border-0 rounded-lg focus:ring-2 focus:ring-[#6b6de3]"
              data-testid="search-input"
            />
          </div>
          <button onClick={openPriceSearch} className="h-9 px-2.5 bg-[#FF9500] rounded-lg flex items-center gap-1 text-white font-medium text-xs shrink-0 hover:bg-[#E88B00] transition-colors" data-testid="price-search-btn">
            <Tag size={14} /> Fiyat
          </button>
          <button onClick={() => setShowNotifications(true)} className="p-2 rounded-full hover:bg-gray-100 transition-colors relative flex-shrink-0" data-testid="header-notifications-btn">
            <Bell size={21} className="text-[#8E8E93]" />
            {unreadNotifCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold text-white bg-[#FF3B30] rounded-full px-0.5" data-testid="notif-badge">
                {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
              </span>
            )}
          </button>
          <button onClick={() => setShowTickets(true)} className="p-2 rounded-full hover:bg-gray-100 transition-colors relative flex-shrink-0" data-testid="header-tickets-btn">
            <MessageSquare size={21} className="text-[#8E8E93]" />
            {unreadTicketCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold text-white bg-[#FF3B30] rounded-full px-0.5" data-testid="ticket-badge">
                {unreadTicketCount}
              </span>
            )}
          </button>
          <button onClick={() => setShowUpdateHistory(true)} className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0" data-testid="update-history-btn">
            <Sparkles size={21} className="text-[#8E8E93]" />
          </button>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between mb-3">
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Ana Sayfa</h1>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setShowNotifications(true)} className="p-2 rounded-full hover:bg-gray-100 transition-colors relative" data-testid="header-notifications-btn-desktop">
              <Bell size={22} className="text-[#8E8E93]" />
              {unreadNotifCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-[#FF3B30] rounded-full px-1">{unreadNotifCount > 99 ? '99+' : unreadNotifCount}</span>
              )}
            </button>
            <button onClick={() => setShowTickets(true)} className="p-2 rounded-full hover:bg-gray-100 transition-colors relative" data-testid="header-tickets-btn-desktop">
              <MessageSquare size={22} className="text-[#8E8E93]" />
              {unreadTicketCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-[#FF3B30] rounded-full px-1">{unreadTicketCount}</span>
              )}
            </button>
            <button onClick={() => setShowTutorial(true)} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-testid="tutorial-btn-desktop">
              <HelpCircle size={22} className="text-[#8E8E93]" />
            </button>
            <button onClick={() => setShowUpdateHistory(true)} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-testid="update-history-btn-desktop">
              <Sparkles size={22} className="text-[#8E8E93]" />
            </button>
          </div>
        </div>
        <div className="hidden lg:flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={20} />
            <Input
              type="search" placeholder="Müşteri Ara (İsim, telefon veya TCKN)"
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && searchResults.length === 1) { handleCustomerSelect(searchResults[0]); } }}
              className="pl-10 h-12 text-base bg-[#F2F2F7] border-0 rounded-xl focus:ring-2 focus:ring-[#6b6de3]"
              data-testid="search-input-desktop"
            />
          </div>
          <button onClick={openPriceSearch} className="h-12 px-4 bg-[#FF9500] rounded-xl flex items-center gap-1.5 text-white font-medium text-sm shrink-0 hover:bg-[#E88B00] transition-colors" data-testid="price-search-btn-desktop">
            <Tag size={18} /> Fiyat
          </button>
        </div>

        {searchQuery.length >= 2 && (
          <div className="mt-3 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden animate-fade-in">
            {searching ? (
              <div className="p-4 flex justify-center"><Loader size="sm" /></div>
            ) : searchResults.length > 0 ? (
              <div className="divide-y divide-gray-100">
                {searchResults.map((customer) => (
                  <button key={customer.id} onClick={() => handleCustomerSelect(customer)} className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-testid={`customer-result-${customer.id}`}>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{customer.name}</p>
                      <p className="text-sm text-[#8E8E93]">{customer.phone}</p>
                      {customer.pets?.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {customer.pets.map(p => (
                            <span key={p.id} className="text-xs text-[#6b6de3] flex items-center gap-0.5">
                              <PetAvatar species={p.species} gender={p.gender} size={10} /> {p.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <ChevronRight size={20} className="text-[#C7C7CC]" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center">
                <p className="text-[#8E8E93] mb-3">Müşteri bulunamadı</p>
                <Button onClick={() => { const q = searchQuery?.trim() || ''; const isNum = /^\d+$/.test(q); setCustomerPrefill({ name: isNum ? '' : q, phone: isNum ? q : '' }); setSearchQuery(''); setSearchResults([]); setShowQuickCustomer(true); }} className="bg-[#6b6de3] hover:bg-[#5658c4] rounded-full text-sm" data-testid="quick-add-btn">
                  <Plus size={18} className="mr-1" /> Hızlı Ekle
                </Button>
              </div>
            )}
          </div>
        )}
      </header>

      <main className="p-4 space-y-3">
        {/* Hızlı Menü */}
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2" data-testid="quick-actions-card">
          <button onClick={() => setShowQuickCustomer(true)} className="bg-white rounded-2xl p-3 flex flex-col items-center gap-1.5 shadow-sm active:scale-95 transition-transform" data-testid="quick-add-customer-btn">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center"><User size={20} className="text-[#6b6de3]" /></div>
            <span className="text-[11px] font-semibold text-gray-900">{`Yeni Müşteri`}</span>
          </button>
          <button onClick={() => setShowQuickPet(true)} className="bg-white rounded-2xl p-3 flex flex-col items-center gap-1.5 shadow-sm active:scale-95 transition-transform" data-testid="quick-add-pet-btn">
            <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center"><PawPrint size={20} className="text-[#FF9500]" /></div>
            <span className="text-[11px] font-semibold text-gray-900">Yeni Hasta</span>
          </button>
          <button onClick={() => setShowQuickSale(true)} className="bg-white rounded-2xl p-3 flex flex-col items-center gap-1.5 shadow-sm active:scale-95 transition-transform" data-testid="quick-new-sale-btn">
            <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center"><Banknote size={20} className="text-[#34C759]" /></div>
            <span className="text-[11px] font-semibold text-gray-900">{`Yeni Satış`}</span>
          </button>
          <button onClick={() => setShowQuickOp(true)} className="bg-white rounded-2xl p-3 flex flex-col items-center gap-1.5 shadow-sm active:scale-95 transition-transform" data-testid="quick-new-operation-btn">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center"><Syringe size={20} className="text-[#AF52DE]" /></div>
            <span className="text-[11px] font-semibold text-gray-900">{`Yeni İşlem`}</span>
          </button>
          <button onClick={() => setShowDirectBooking(true)} className="bg-white rounded-2xl p-3 flex flex-col items-center gap-1.5 shadow-sm active:scale-95 transition-transform" data-testid="quick-new-appointment-btn">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center"><CalendarPlus size={20} className="text-[#5856D6]" /></div>
            <span className="text-[11px] font-semibold text-gray-900">Yeni Randevu</span>
          </button>
          <button onClick={() => setShowQuickBalance(true)} className="bg-white rounded-2xl p-3 flex flex-col items-center gap-1.5 shadow-sm active:scale-95 transition-transform" data-testid="quick-balance-btn">
            <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center"><Wallet size={20} className="text-[#30B0C7]" /></div>
            <span className="text-[11px] font-semibold text-gray-900">{`Hızlı Muhasebe`}</span>
          </button>
        </div>

        <DailySummary stats={stats} showIncome={showIncome} setShowIncome={setShowIncome} formatCurrency={formatCurrency} setHistoryType={(type) => setHistoryModal({ mode: type, date: new Date().toISOString().split('T')[0] })} onPaymentTypeClick={(type) => setHistoryModal({ mode: type, date: new Date().toISOString().split('T')[0] })} boardingCount={boardingCount} />
        <RemindersSection todaysVaccines={todaysVaccines} upcomingVaccines={upcomingVaccines} navigate={navigate} />
        <AlertsSection stockAlerts={stockAlerts} navigate={navigate} />
      </main>

      <BottomNav />

      <QuickSaleModal open={showQuickSale} onClose={(refresh) => { setShowQuickSale(false); if (refresh) loadData(); }} />
      <QuickCustomerModal
        open={showQuickCustomer}
        onClose={(refresh) => { setShowQuickCustomer(false); if (refresh) loadData(); }}
        prefillName={customerPrefill.name}
        prefillPhone={customerPrefill.phone}
      />
      <QuickPetModal open={showQuickPet} onClose={(refresh) => { setShowQuickPet(false); if (refresh) loadData(); }} />
      <QuickOperationModal open={showQuickOp} onClose={(refresh) => { setShowQuickOp(false); if (refresh) loadData(); }} />
      <DirectBookingModal open={showDirectBooking} onOpenChange={setShowDirectBooking} onCreated={() => loadData()} />
      <QuickBalanceModal open={showQuickBalance} onClose={(refresh) => { setShowQuickBalance(false); if (refresh) loadData(); }} />

      <PriceSearchModal
        open={showPriceSearch}
        onClose={() => { setShowPriceSearch(false); setPriceQuery(''); }}
        priceQuery={priceQuery}
        setPriceQuery={setPriceQuery}
        filteredPriceResults={filteredPriceResults}
      />

      <UnifiedHistoryModal
        open={!!historyModal}
        onClose={() => setHistoryModal(null)}
        mode={historyModal?.mode}
        date={historyModal?.date}
        dateFrom={historyModal?.dateFrom}
        dateTo={historyModal?.dateTo}
      />
      <NotificationsModal open={showNotifications} onClose={() => setShowNotifications(false)} onCountChange={setUnreadNotifCount} />
      <TicketsModal open={showTickets} onClose={() => setShowTickets(false)} onCountChange={setUnreadTicketCount} />
      {showTutorial && <TutorialModal onClose={() => setShowTutorial(false)} />}
      <UpdateHistoryModal open={showUpdateHistory} onClose={() => setShowUpdateHistory(false)} />

      <SubscriptionWarningOverlay warning={subscriptionWarning} navigate={navigate} onDismiss={() => setSubscriptionWarning(null)} />
    </div>
  );
};

export default Home;
